// -------------------------------------------------------------------------------------------------
// Functions
export const foldFromProp = (p) => {
    const foldStrict = (...args) => {
        if (args.length === 1) {
            const [pattern] = args;
            return (value) => foldStrict(value, pattern);
        }
        const [value, pattern] = args;
        const tag = value[p];
        return pattern[tag](value);
    };
    return foldStrict;
};
/**
 * Build a function to pattern match on discriminated union type.
 *
 * @example: Data first
 * interface A { _tag: 'A', a: number }
 * interface B { _tag: 'B', b: string }
 * type MyType = A | B
 *
 * const foldMyType = foldFromTags<MyType>('_tag')
 * const result = foldMyType(
 *   { _tag: 'A', a: 1},
 *   {
 *     A: ({ a }) => a * 2,
 *     B: ({ b }) => parseInt(b, 10)
 *   }
 * )
 *
 * @example: Data last
 * interface A { _tag: 'A', a: number }
 * interface B { _tag: 'B', b: string }
 * type MyType = A | B
 *
 * const foldMyType = foldFromTags<MyType, '_tag'>('_tag')
 * const result = pipe(
 *   { _tag: 'A', a: 1},
 *   foldMyType({
 *     A: ({ a }) => a * 2,
 *     B: ({ b }) => parseInt(b, 10)
 *   })
 * )
 */
export const foldFromTags = foldFromProp;
/**
 * Build a function to pattern match on a primitive union type (union of strings).
 *
 * @example: Data first
 * const foldMyUnion = foldFromKeys({ A: null, B: null })
 * const result = foldMyUnion('A', { A: () => 'a', B: () => 'b' })
 *
 * @example: Data last
 * const foldMyUnion = foldFromKeys({ A: null, B: null })
 * const result = pipe(
 *   'A',
 *   foldMyUnion({ A: () => 'a', B: () => 'b' })
 * )
 *
 * @example: Use the union's elements as an array
 * const foldMyUnion = foldFromKeys({ A: null, B: null })
 * const myUnionKeys = foldMyUnion.keys // -> ['A', 'B']
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function foldFromKeys(spec) {
    function fold(arg1, arg2) {
        if (arg2 == null)
            return (key) => fold(key, arg1);
        return arg2[arg1]();
    }
    fold.spec = spec;
    fold.keys = Object.keys(spec);
    return fold;
}
/**
 * Similar to tagged.build().is, this creates a record with type-refinements.
 *
 * @example
 * interface A { _tag: 'A', a: number }
 * interface B { _tag: 'B', b: string }
 * type AB = A | B
 *
 * const abIs = refinementFromProp<AB, '_tag'>('_tag');
 * declare const ab: AB;
 * if (abIs.B(ab)) {
 *   console.log(ab.b.toUpperCase())
 * }
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const refinementFromProp = (p) => {
    const a = {};
    return new Proxy(a, {
        get(target, prop) {
            if (typeof prop !== 'string')
                return Reflect.get(target, prop);
            const pValue = prop;
            if (!(pValue in target)) {
                target[pValue] = ((a) => a[p] === pValue);
            }
            return target[pValue];
        },
    });
};
export const valuesFromKeys = () => new Proxy({}, { get: (_, prop) => prop });
//# sourceMappingURL=adt.js.map